// ante-react-kit/src/components/v2/AntHeaderCard/index.js
import React, { useMemo, useRef, useCallback, useState } from 'react';
import './index.scss';
import AntBaseForm from '../../AntBaseForm';
import SummaryPanel from './SummaryPanel';
import PickerDisplay from './PickerDisplay';
import AntEntityPicker from '../AntEntityPicker';
import AntAddressPicker from '../AntAddressPicker';
import AntLocalization from '../../../utils/AntLocalization';
import AntCondition from '../../../utils/AntCondition';
import { getStatusTheme } from './StatusTheme';

export default function AntHeaderCard(props) {
    var { sections, items, formData, oldData, status, statusField,
          API, session, onFieldDataChanged, disabled, table } = props;

    var theme = getStatusTheme(status);

    var entityPickerRef = useRef(null);
    var addressPickerRef = useRef(null);

    // Classify sections
    var classified = useMemo(function () {
        if (!sections) return { fixed: [], tabs: [], summary: null };
        var fixed = [];
        var tabs = [];
        var summary = null;
        sections.forEach(function (sec) {
            if (sec.priority === 'summary') summary = sec;
            else if (sec.priority === 'fixed') fixed.push(sec);
            else if (sec.priority === 'tab') tabs.push(sec);
            else fixed.push(sec); // fallback
        });
        return { fixed: fixed, tabs: tabs, summary: summary };
    }, [sections]);

    // Tab state
    var [activeTab, setActiveTab] = useState(function () {
        return classified.tabs.length > 0 ? classified.tabs[0].name : '';
    });

    // Flatten all items from groups
    var allFields = useMemo(function () {
        if (!items) return [];
        var flat = [];
        function walk(arr) {
            if (!arr) return;
            arr.forEach(function (item) {
                if (item.itemType === 'group' && item.items) {
                    walk(item.items);
                } else {
                    flat.push(item);
                }
            });
        }
        walk(items);
        return flat;
    }, [items]);

    // Filter items cho mỗi section
    var sectionItems = useMemo(function () {
        if (!sections || !allFields.length) return {};
        var result = {};
        sections.forEach(function (sec) {
            var secFields = (sec.fields || []).concat(sec.addressFields || []);
            result[sec.name] = allFields.filter(function (item) {
                return secFields.indexOf(item.dataField) >= 0;
            });
        });
        return result;
    }, [sections, allFields]);

    // Picker callbacks
    var onEntitySelect = useCallback(function (entity) {
        if (!onFieldDataChanged || !entity) return;
        var mappings = {
            entity_name: entity.entity_name || entity.name,
            entity_contact_phone: entity.entity_contact_phone || entity.phone,
            entity_email: entity.entity_email || entity.email,
            entity_legal_id: entity.entity_legal_id || entity.legal_id
        };
        Object.keys(mappings).forEach(function (field) {
            if (mappings[field] !== undefined) {
                onFieldDataChanged({ dataField: field, value: mappings[field] });
            }
        });
    }, [onFieldDataChanged]);

    var onAddressSelect = useCallback(function (addr) {
        if (!onFieldDataChanged || !addr) return;
        var mappings = {
            transport_contact_name: addr.contact_name || addr.transport_contact_name,
            transport_contact_phone: addr.contact_phone || addr.transport_contact_phone,
            transport_address: addr.address || addr.transport_address,
            transport_country_code: addr.country_code || addr.transport_country_code,
            transport_postal_code: addr.postal_code || addr.transport_postal_code,
            transport_adm1_code: addr.adm1_code || addr.transport_adm1_code,
            transport_adm1_name: addr.adm1_name || addr.transport_adm1_name,
            transport_adm2_code: addr.adm2_code || addr.transport_adm2_code,
            transport_adm2_name: addr.adm2_name || addr.transport_adm2_name,
            transport_adm3_code: addr.adm3_code || addr.transport_adm3_code,
            transport_adm3_name: addr.adm3_name || addr.transport_adm3_name
        };
        Object.keys(mappings).forEach(function (field) {
            if (mappings[field] !== undefined) {
                onFieldDataChanged({ dataField: field, value: mappings[field] });
            }
        });
    }, [onFieldDataChanged]);

    var openEntityPicker = useCallback(function (e) {
        if (e) e.stopPropagation();
        if (entityPickerRef.current) entityPickerRef.current.show();
    }, []);

    var openAddressPicker = useCallback(function (e) {
        if (e) e.stopPropagation();
        if (addressPickerRef.current) addressPickerRef.current.show();
    }, []);

    // Render section body content
    function renderSectionBody(section) {
        var secItems = sectionItems[section.name] || [];
        var hasPicker = section.entityPicker || section.addressPicker;

        if (hasPicker) {
            var pickerFields = section.fields || [];
            if (section.addressFields) pickerFields = pickerFields.concat(section.addressFields);

            if (section.addressPicker && section.fields && section.fields.length > 0) {
                var addrFields = section.addressFields || [];
                var otherItems = secItems.filter(function (item) {
                    return addrFields.indexOf(item.dataField) < 0;
                });
                return (
                    <React.Fragment>
                        <PickerDisplay
                            fields={addrFields}
                            formData={formData}
                            table={table}
                            emptyText={{ vi: 'Chưa có địa chỉ giao hàng', en: 'No delivery address', fr: 'Aucune adresse de livraison', es: 'Sin dirección de entrega', id: 'Belum ada alamat pengiriman', ja: '配送先住所なし', zh: '暂无收货地址', ko: '배송 주소 없음', hi: 'कोई डिलीवरी पता नहीं', ar: 'لا يوجد عنوان تسليم' }}
                            onPickerClick={disabled ? undefined : openAddressPicker}
                        />
                        {otherItems.length > 0 && (
                            <AntBaseForm
                                type="Update" table={table} API={API}
                                items={otherItems} formData={formData} oldData={oldData}
                                disabled={disabled} onFieldDataChanged={onFieldDataChanged}
                                session={session} labelLocation="top" colCount={2} hideBorder={true}
                            />
                        )}
                    </React.Fragment>
                );
            }
            return (
                <PickerDisplay
                    fields={pickerFields}
                    formData={formData}
                    table={table}
                    emptyText={{ vi: 'Chưa chọn khách hàng', en: 'No customer selected', fr: 'Aucun client choisi', es: 'Sin cliente seleccionado', id: 'Belum pilih pelanggan', ja: '顧客が未選択です', zh: '未选择客户', ko: '고객 미선택', hi: 'कोई ग्राहक नहीं चुना', ar: 'لم يُحدد عميل' }}
                    onPickerClick={disabled ? undefined : (section.entityPicker ? openEntityPicker : openAddressPicker)}
                />
            );
        }

        if (secItems.length === 0) return null;

        return (
            <AntBaseForm
                type="Update" table={table} API={API}
                items={secItems} formData={formData} oldData={oldData}
                disabled={disabled} onFieldDataChanged={onFieldDataChanged}
                session={session} labelLocation="top" colCount={2} hideBorder={true}
            />
        );
    }

    if (!sections || !formData) return null;

    var summaryItems = classified.summary ? (sectionItems[classified.summary.name] || []) : [];

    return (
        <div className="v2-header-card" style={{ opacity: theme.textOpacity }}>
            {/* Left: stack (fixed sections + tabs) */}
            <div className="v2-header-card__stack">
                {/* Fixed sections (always visible, no gap) */}
                {classified.fixed.map(function (sec) {
                    return (
                        <div key={sec.name} className="v2-stack-section">
                            <div className="v2-stack-section__header">
                                {sec.icon && <i className={'dx-icon-' + sec.icon + ' v2-stack-section__icon'} />}
                                <span className="v2-stack-section__title">{AntLocalization.translateText(sec.title)}</span>
                            </div>
                            <div className="v2-stack-section__body">
                                {renderSectionBody(sec)}
                            </div>
                        </div>
                    );
                })}

                {/* Tab bar */}
                {classified.tabs.length > 0 && (
                    <div className="v2-header-tabs">
                        <div className="v2-header-tabs__bar">
                            {classified.tabs.map(function (tab) {
                                var isActive = activeTab === tab.name;
                                // Picker button inline trong tab
                                var hasEP = tab.entityPicker && !disabled;
                                var hasAP = tab.addressPicker && !disabled;
                                return (
                                    <button
                                        key={tab.name}
                                        className={'v2-header-tabs__tab' + (isActive ? ' v2-header-tabs__tab--active' : '')}
                                        onClick={function () { setActiveTab(tab.name); }}
                                    >
                                        {tab.icon && <i className={'dx-icon-' + tab.icon + ' v2-header-tabs__tab-icon'} />}
                                        <span>{AntLocalization.translateText(tab.title)}</span>
                                        {hasEP && (
                                            <i className="dx-icon-search v2-header-tabs__picker-icon" onClick={openEntityPicker} title="Tìm khách hàng" />
                                        )}
                                        {hasAP && (
                                            <i className="dx-icon-map v2-header-tabs__picker-icon" onClick={openAddressPicker} title="Chọn địa chỉ" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="v2-header-tabs__content">
                            {classified.tabs.map(function (tab) {
                                var isActive = activeTab === tab.name;
                                return (
                                    <div key={tab.name} className="v2-header-tabs__panel" style={{ display: isActive ? 'block' : 'none' }}>
                                        {renderSectionBody(tab)}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Right: summary */}
            {classified.summary && (
                <div className="v2-header-card__summary" style={{ borderLeftColor: theme.accent }}>
                    <div className="v2-stack-section__header">
                        {classified.summary.icon && <i className={'dx-icon-' + classified.summary.icon + ' v2-stack-section__icon'} />}
                        <span className="v2-stack-section__title">{AntLocalization.translateText(classified.summary.title)}</span>
                    </div>
                    <div className="v2-header-card__summary-body">
                        <SummaryPanel
                            fields={classified.summary.fields}
                            items={summaryItems}
                            formData={formData}
                            table={table}
                            status={status}
                            session={session}
                        />
                    </div>
                </div>
            )}

            {/* Pickers */}
            <AntEntityPicker ref={entityPickerRef} API={API} session={session} onSelect={onEntitySelect} />
            <AntAddressPicker
                ref={addressPickerRef} API={API} session={session}
                onSelect={onAddressSelect}
                savedAddresses={formData ? formData.saved_addresses : null}
            />
        </div>
    );
}
